import React, { useEffect, useState } from "react";
import { Modal, message } from "antd";
import Button from "antd-button-color";
import { BsPencil } from "react-icons/bs";
import {
  HiOutlineCamera,
  HiOutlineUser,
  HiOutlineAtSymbol,
  HiOutlineInformationCircle,
  HiOutlineLocationMarker,
} from "react-icons/hi";
import { DeleteOutlined } from "@ant-design/icons";
import axios from "axios";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";

import { selectUser } from "../../redux/slices/auth.slice";
import IconButton from "../buttons/IconButton";
import ImageUploadButton from "../inputs/ImageUploadButton";
import fupload from "../../resources/feather-upload.svg";
import { cloudFrontUrl } from "../../nestserver";

const IMAGE_HEIGHT = 280;
const AVATAR_SIZE = 168;
const BANNER_GRADIENT =
  "linear-gradient(135deg, #10AADD 0%, #09617E 55%, #03212B 100%)";

function getInitials(firstName, lastName) {
  return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.toUpperCase();
}

function avatarFallback(firstName, lastName) {
  return (
    "https://ui-avatars.com/api/?name=" +
    encodeURIComponent(`${firstName ?? ""} ${lastName ?? ""}`)
  );
}

function InfoRow({ icon, children }) {
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}
    >
      <span style={{ color: "#09617E", flexShrink: 0, display: "flex" }}>
        {icon}
      </span>
      {children}
    </div>
  );
}

export default function ProfileBanner({
  userId,
  roleData,
  patchUrl,
  EditFormComponent,
  extraContent,
  subtitle,
  readOnly = false,
  onUpdated,
}) {
  const queryClient = useQueryClient();
  const school = useSelector(selectUser)?.school;

  const [bgModal, setBgModal] = useState(false);
  const [profileModal, setProfileModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [images, setImages] = useState({
    profile_image: null,
    banner_image: null,
  });

  useEffect(() => {
    setImages({
      profile_image: roleData?.profile_image ?? null,
      banner_image: roleData?.banner_image ?? null,
    });
  }, [roleData]);

  function resolveImageUrl(filename) {
    return filename ? `${cloudFrontUrl}/${userId}/${filename}` : null;
  }

  function updateProfile() {
    axios({
      method: "patch",
      url: patchUrl,
      data: { ...images },
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    })
      .then(() => {
        message.success("Profile Updated");
        queryClient.invalidateQueries("user");
        setProfileModal(false);
        setBgModal(false);
        onUpdated?.();
      })
      .catch((e) => {
        message.error(e.message);
      });
  }

  // School-wide default set by the admin; used only when the user has no banner.
  const schoolBannerUrl =
    school?.default_banner_image && school?.default_banner_folder
      ? `${cloudFrontUrl}/${school.default_banner_folder}/${school.default_banner_image}`
      : null;
  const bannerUrl = resolveImageUrl(images.banner_image) || schoolBannerUrl;
  const avatarUrl =
    resolveImageUrl(roleData?.profile_image) ||
    avatarFallback(roleData?.first_name, roleData?.last_name);
  const previewAvatarUrl =
    resolveImageUrl(images.profile_image) ||
    avatarFallback(roleData?.first_name, roleData?.last_name);
  const initials = getInitials(roleData?.first_name, roleData?.last_name);
  const hasLocation = Boolean(roleData?.region || roleData?.country);

  return (
    <div
      style={{ borderRadius: "10px", overflow: "hidden" }}
      className="bg-white drop-shadow-md"
    >
      <div
        style={{
          height: IMAGE_HEIGHT,
          background: bannerUrl
            ? `url("${bannerUrl}") center / cover no-repeat`
            : BANNER_GRADIENT,
        }}
        className="relative"
      >
          {!bannerUrl && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  color: "rgba(255,255,255,0.28)",
                  fontSize: "4.5rem",
                  fontWeight: 700,
                  letterSpacing: "0.05em",
                  userSelect: "none",
                }}
              >
                {initials}
              </span>
            </div>
          )}

          <div
            style={{
              position: "absolute",
              bottom: 24,
              left: 24,
              width: AVATAR_SIZE,
              height: AVATAR_SIZE,
              borderRadius: "9999px",
              border: "8px solid white",
              background: "#fff",
            }}
          >
            <img
              className="w-full h-full rounded-full object-cover"
              src={avatarUrl}
              alt="profile"
            />
            {!readOnly && (
              <div
                className="absolute cursor-pointer"
                style={{ bottom: -4, right: -4 }}
                onClick={() => setProfileModal(true)}
              >
                <IconButton icon={<HiOutlineCamera size={20} />} />
              </div>
            )}
          </div>

          {!readOnly && (
            <div className="absolute top-5 right-5">
              <IconButton
                icon={<BsPencil />}
                onClick={() => setBgModal(true)}
              />
            </div>
          )}

          {!readOnly && (
            <Modal
              width={800}
              open={bgModal}
              onOk={() => setBgModal(false)}
              onCancel={() => setBgModal(false)}
              footer={null}
            >
              <div>
                <div className="py-2">
                  <div className="text-2xl font-bold text-black pb-4 capitalize">
                    Cover Photo
                  </div>

                  <div className="flex items-center justify-center">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        aspectRatio: "9 / 2.5",
                        maxHeight: 400,
                        borderRadius: 8,
                        overflow: "hidden",
                        background: bannerUrl ? "#f3f4f6" : BANNER_GRADIENT,
                      }}
                    >
                      {bannerUrl ? (
                        <img
                          className="w-full h-full object-contain"
                          src={bannerUrl}
                          alt="banner preview"
                        />
                      ) : (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <span
                            style={{
                              color: "rgba(255,255,255,0.28)",
                              fontSize: "2.5rem",
                              fontWeight: 700,
                            }}
                          >
                            {initials}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-3">
                  <div className="flex flex-row space-x-5">
                    <ImageUploadButton
                      folder={userId}
                      ratio={9 / 2.5}
                      enableCrop
                      maxDimension={1600}
                      onUpload={(id) =>
                        setImages((i) => ({ ...i, banner_image: id }))
                      }
                    >
                      <div
                        style={{ color: "#616161" }}
                        className="flex flex-row space-x-4 cursor-pointer"
                      >
                        <img src={fupload} alt="" />
                        <div className="text-lg">Upload photo</div>
                      </div>
                    </ImageUploadButton>
                    <Button
                      onClick={() =>
                        setImages((i) => ({ ...i, banner_image: null }))
                      }
                      type="danger"
                      className="flex items-center"
                      icon={<DeleteOutlined />}
                    >
                      Remove
                    </Button>
                  </div>

                  <div className="inline-flex items-center space-x-2">
                    <Button
                      type="dark"
                      onClick={() => {
                        setBgModal(false);
                        setImages((i) => ({
                          ...i,
                          banner_image: roleData?.banner_image ?? null,
                        }));
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      type="primary"
                      htmlType="submit"
                      className="text-white"
                      onClick={updateProfile}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </Modal>
          )}
        </div>

      <div style={{ padding: "20px 24px", position: "relative" }}>
        {!readOnly && EditFormComponent && (
          <div className="absolute" style={{ top: 16, right: 16 }}>
            <IconButton
              icon={<BsPencil />}
              onClick={() => setEditModal(true)}
            />
          </div>
        )}

        <InfoRow icon={<HiOutlineUser size={20} />}>
          <span style={{ fontSize: "1.15rem", fontWeight: 700 }}>
            {roleData?.first_name} {roleData?.last_name}
          </span>
        </InfoRow>

        {subtitle && (
          <InfoRow icon={<HiOutlineAtSymbol size={18} />}>
            <span style={{ fontSize: "0.9rem", color: "#666" }}>
              {subtitle}
            </span>
          </InfoRow>
        )}

        {roleData?.headline && (
          <InfoRow icon={<HiOutlineInformationCircle size={18} />}>
            <span style={{ fontSize: "1rem", wordBreak: "break-word" }}>
              {roleData.headline}
            </span>
          </InfoRow>
        )}

        {hasLocation && (
          <InfoRow icon={<HiOutlineLocationMarker size={18} />}>
            <span style={{ fontWeight: 700, fontSize: "0.9rem" }}>
              {roleData?.region && roleData.region + ", "}
              {roleData?.country}
            </span>
          </InfoRow>
        )}

        {extraContent}

        {!readOnly && (
          <Modal
            width={800}
            open={profileModal}
            onOk={() => setProfileModal(false)}
            onCancel={() => setProfileModal(false)}
            footer={null}
          >
            <div>
              <div className="py-2">
                <div className="text-2xl font-bold text-black pb-4 capitalize">
                  Profile Photo
                </div>

                <div className="flex items-center justify-center">
                  <div className="relative w-44 h-44 rounded-full border-8 border-white overflow-hidden">
                    <img
                      className="w-full h-full object-cover"
                      src={previewAvatarUrl}
                      alt="profile preview"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center pt-3">
                <div className="flex flex-row space-x-5">
                  <ImageUploadButton
                    folder={userId}
                    ratio={1}
                    enableCrop
                    cropShape="round"
                    maxDimension={500}
                    onUpload={(id) =>
                      setImages((i) => ({ ...i, profile_image: id }))
                    }
                  >
                    <div
                      style={{ color: "#616161" }}
                      className="flex flex-row space-x-4 cursor-pointer"
                    >
                      <img src={fupload} alt="" />
                      <div className="text-lg">Upload photo</div>
                    </div>
                  </ImageUploadButton>
                  <Button
                    onClick={() =>
                      setImages((i) => ({ ...i, profile_image: null }))
                    }
                    type="danger"
                    className="flex items-center"
                    icon={<DeleteOutlined />}
                  >
                    Remove
                  </Button>
                </div>

                <div className="inline-flex items-center space-x-2">
                  <Button
                    size="large"
                    type="dark"
                    onClick={() => {
                      setProfileModal(false);
                      setImages((i) => ({
                        ...i,
                        profile_image: roleData?.profile_image ?? null,
                      }));
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    size="large"
                    type="primary"
                    htmlType="submit"
                    className="text-white"
                    onClick={updateProfile}
                  >
                    Save
                  </Button>
                </div>
              </div>
            </div>
          </Modal>
        )}

        {!readOnly && EditFormComponent && (
          <Modal
            width={800}
            open={editModal}
            footer={null}
            onOk={() => setEditModal(false)}
            onCancel={() => setEditModal(false)}
          >
            <EditFormComponent
              data={roleData}
              closeModel={() => setEditModal(false)}
            />
          </Modal>
        )}
      </div>
    </div>
  );
}
